import {GameScreen} from './GameScreen';
import {GameSplashScreen} from './GameSplashScreen';
import {GameWaiter} from './GameWaiter';
import {GameDialog} from './GameDialog';
import {GameDispatcher} from './GameDispatcher';
import {GameDialogLayer, GameScreenLayer} from '../layers';
import {GameObject} from "./GameObject";
import {ArrayUtils} from "../../utils/ArrayUtils";
import {ObjectUtils} from "../../utils/ObjectUtils";
import {WaiterLayer} from "../layers/WaiterLayer";
import {SplashLayer} from "../layers/SplashLayer";
import {UILayer} from "../layers/UILayer";
import {AbstractInfoDialog} from "../dialogs/AbstractInfoDialog";
import {Rectangle} from "pixi.js";

export class GameScreenNavigator extends GameScreen {

  protected _splashScreenClass: (new () => GameSplashScreen) | null = null;  // Class type for splash screen
  protected _splashScreen: GameSplashScreen | null = null;
  protected _isScreenChangingProcess: boolean = false;
  protected _gameWaiter: GameWaiter | null = null;

  protected _screenLayer: GameScreenLayer = new GameScreenLayer();
  protected _uiLayer = new UILayer();
  protected _dialogLayer = new GameDialogLayer();
  protected _splashLayer = new SplashLayer();
  protected _waiterLayer = new WaiterLayer();
  protected _incomingScreen: GameScreen | null = null;
  protected _dialogsQueue: GameDialog[] = [];

  constructor() {
    super();
    this.addChild(this._screenLayer);
    this.addChild(this._uiLayer);
    this.addChild(this._dialogLayer);
    this.addChild(this._splashLayer);
    this.addChild(this._waiterLayer);
  }

  protected _currentScreen: GameScreen | null = null;

  override getAreaForHtml(){
    return this._currentScreen?.getAreaForHtml() || new Rectangle();
  }

  public get currentScreen(): GameScreen | null {
    return this._currentScreen;
  }

  public getScreenFromHierarchy(targetClass: any):any {
    for (const child of this._screenLayer.children) {
      // console.log(child.constructor.name);

      if (child instanceof targetClass) {
        return child;
      }
      if (child instanceof GameScreenNavigator) {
        let result =  (child as GameScreenNavigator).getScreenFromHierarchy(targetClass);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  public get isLoadingIncomingScreen(): boolean {
    return Boolean(this._incomingScreen);
  }

  override getStateDescription(): string {
    return `GameScreenNavigator: currentScreen: ${this._currentScreen ? this._currentScreen.getStateDescription() : '---'}`;
  }

  public setSplashScreenClass(splashScreenClass: (new () => GameSplashScreen)): void {
    this._splashScreenClass = splashScreenClass;
  }

  override destroy(): void {
    this.removeAllDialogs();
    super.destroy();
    this._splashScreen = GameObject.destroy(this._splashScreen);
    GameObject.destroy(this._uiLayer);
    GameObject.destroy(this._screenLayer);
    GameObject.destroy(this._dialogLayer);
    GameObject.destroy(this._splashLayer);
    GameObject.destroy(this._waiterLayer);
    GameObject.destroy(this._incomingScreen);
  }

  public showDialog(dialog: GameDialog, enqueue: boolean = false, replaceOthersOfSameType: boolean = false): void {
    this.root.app.executeWhenApplicationIsActive(() => {
      if (this.destroyed) {
        return;
      }
      if (!dialog || dialog.destroyed || dialog.parent || !this.root.assetsLoader?.isLoaded) {
        return;
      }

      dialog.root = this.root;

      if (replaceOthersOfSameType) {
        this._dialogLayer.hideAllDialogsWithType(ObjectUtils.getQualifiedClassName(dialog));
      }
      if (!enqueue || !this._dialogsQueue.length) {
        if (dialog instanceof AbstractInfoDialog) {
          this._waiterLayer.addChild(dialog);
        } else {
          this._dialogLayer.addChild(dialog);
        }
        dialog.loadAssets(dialog.onAdded.bind(dialog));
        dialog._shouldDisposeCallback = this.onDialogRemove.bind(this);
      }
      if (enqueue && !this._dialogsQueue.includes(dialog)) {
        this._dialogsQueue.push(dialog);
      }
    });
  }

  public async changeScreen<T extends GameScreen>(screen: T, withSplash: boolean) {
    if (this.destroyed) {
      return;
    }
    if (this._splashScreen) {
      return;
    }
    if (this._incomingScreen || this._isScreenChangingProcess) {
      return;
    }

    screen.root = this.root;

    this._incomingScreen = screen;
    this._isScreenChangingProcess = true;

    this.root.app.executeWhenApplicationIsActive(async () => {
      if (withSplash && this._splashScreenClass) {
        this._splashScreen = new this._splashScreenClass();
        this._splashScreen.root = this.root;
        await this._splashScreen!.loadAssets(this.showSplashScreen.bind(this));
      } else {
        this.loadIncomingScreen();
      }
    });
  }

  public showWaiter(): void {
    if (!this._gameWaiter) {
      this._gameWaiter = new GameWaiter(this.root, this._waiterLayer);
    }
    this.root.app.executeWhenApplicationIsActive(this._gameWaiter.show.bind(this._gameWaiter));
  }

  public hideWaiter(): void {
    if (this._gameWaiter) {
      this.root.app.executeWhenApplicationIsActive(this._gameWaiter.hide.bind(this._gameWaiter));
    }
  }

  public removeAllDialogs(): void {
    if (this.children) {
      for (let i = this.children.length - 1; i >= 0; i--) {
        let child = this.children[i];
        if (child instanceof GameScreenNavigator) {
          (child as GameScreenNavigator).removeAllDialogs();
        }
      }
    }
    if (this._dialogsQueue) {
      GameObject.destroy(this._dialogsQueue);
    }
    if (this._dialogLayer) {
      this._dialogLayer.destroyChildren();
    }
  }

  public destroySplash(): void {
    this._splashScreen = GameObject.destroy(this._splashScreen);
  }

  protected loadIncomingScreen(): void {
    if (this._currentScreen) {
      this._currentScreen = GameObject.destroy(this._currentScreen);
    }
    if (this._incomingScreen) {
      setTimeout(() => this._incomingScreen?.loadAssets(this.onIncomingScreenLoaded.bind(this)), 50); // time for GC
    }
  }

  protected async onIncomingScreenLoaded() {
    this._currentScreen = this._incomingScreen;
    this._isScreenChangingProcess = false;
    this._incomingScreen = null;

    if (this.currentScreen) {
      this._screenLayer.addChild(this.currentScreen);
      await this.currentScreen.onAdded();
      this.root.onScreenHierarchyChanged.dispatch();
    }

    if (this._splashScreen) {
      this._splashScreen.hide();
    }

    // if (DeviceManager.isLowMemory()) {
    //   this.showDialog(new ErrorDialog(Locale.getText(Locale.low_device_memory)));
    // }
  }

  private onDialogRemove(dialog: GameDialog): void {
    this.root.app.executeWhenApplicationIsActive(() => {
      if (this.destroyed) {
        return;
      }
      ArrayUtils.remove(this._dialogsQueue, dialog);
      GameObject.destroy(dialog);
      if (this._dialogsQueue && this._dialogsQueue.length) {
        if (!this.root.app.error.isUncaughtErrorOccurred) {
          let dialogToShow = this._dialogsQueue.shift();
          if(dialogToShow && !dialogToShow.parent) {
            this.showDialog(dialogToShow);
          }
        }
      }
    });
  }

  private showSplashScreen(): void {
    this._splashLayer.addChild(this._splashScreen!);
    this._splashScreen!.onShowed = this.loadIncomingScreen.bind(this);
    this._splashScreen!.onHide = this.destroySplash.bind(this);
    this._splashScreen!.onAdded();
  }

  override onActivate(): void {
    this._currentScreen?.onActivate();
  }

  override onDeActivate(): void {
    this._currentScreen?.onDeActivate();
  }

  override onExit(): void {
    this._currentScreen?.onExit();
  }

  override onResize(): void {
    this._screenLayer?.onResize();
    this._uiLayer?.onResize();
    this._dialogLayer?.onResize();
    this._splashLayer?.onResize();
    this._waiterLayer?.onResize();
  }
}
